package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Video

/** Shared title + author for promo generation, hoisted into a sidebar
 *  card so the inputs sit out of the main column's flow. Both
 *  PromoSettingsCard (sidebar) and PromoTabs (main) read/write the
 *  same holder via [LocalPromoSettings].
 *
 *  When [bookId] is non-null the inputs are persisted to localStorage
 *  so a page refresh — or closing the browser entirely — doesn't drop
 *  the user's typed values (bug #22). Wiped on logout via
 *  [AppDataStore.clearTokens]. Use [setTitle] / [setAuthor] — direct
 *  field assignment is intentionally not supported, since the
 *  persistence must run on every change.
 */
class PromoSettings(private val bookId: String?) {
    var title by mutableStateOf(bookId?.let { AppDataStore.getPromoTitle(it) }.orEmpty())
        private set
    var author by mutableStateOf(bookId?.let { AppDataStore.getPromoAuthor(it) }.orEmpty())
        private set
    /** Bumped by Cover/Teaser tabs when a generate-click hits an empty
     *  title; the sidebar input flashes red for a moment to point the
     *  user back at the missing field. */
    var titleHighlightTick by mutableStateOf(0)

    fun setTitle(value: String) {
        title = value
        if (bookId != null) AppDataStore.setPromoTitle(bookId, value)
    }

    fun setAuthor(value: String) {
        author = value
        if (bookId != null) AppDataStore.setPromoAuthor(bookId, value)
    }
}

val LocalPromoSettings = staticCompositionLocalOf<PromoSettings> {
    error("LocalPromoSettings not provided — wrap children with " +
          "`CompositionLocalProvider(LocalPromoSettings provides …)` " +
          "in ResultsArea")
}

/** Sidebar card that owns the «title + author» inputs used by both
 *  the cover and the teaser tabs. Lives in the sidebar so the two
 *  full-width text fields don't eat vertical space in the centre of
 *  the page. The state is read by PromoTabs via [LocalPromoSettings]. */
@Composable
fun PromoSettingsCard() {
    val s = LocalStrings.current
    val settings = LocalPromoSettings.current
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 20)
            flexCol(gapPx = 10)
            fillWidth()
            property("box-sizing", "border-box")
            property("position", "relative")
        }
    }) {
        BlockHelpCorner(s.helpPromoSettingsTitle, s.helpPromoSettingsBody, id = AnalyticsEvents.HelpBlock.PROMO_SETTINGS)
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.4px")
                property("padding-right", "26px")
            }
        }) { Text(s.promoSettingsLabel) }
        CoverInput(
            label = s.promoCoverTitleLabel,
            value = settings.title,
            onChange = { settings.setTitle(it) },
            highlightTick = settings.titleHighlightTick,
            maxLength = 60,
        )
        CoverInput(
            label = s.promoCoverAuthorLabel,
            value = settings.author,
            onChange = { settings.setAuthor(it) },
            maxLength = 60,
        )
    }
}

/**
 * UI-блок «Промо-материалы» — таб-бар с тремя вкладками: аннотация,
 * обложка, видео (storyboard-first книжный тизер). Все три — real
 * генерация через `MainViewModel.generate*()`; состояние приходит
 * из VM, локальных mock-стейтов нет.
 */
@Composable
fun PromoMaterialsSection(
    aiState: AnalysisState,
    aiReport: AiReport?,
    annotation: AnnotationResult?,
    annotationLoading: Boolean,
    annotationError: String?,
    onGenerateAnnotation: () -> Unit,
    covers: Map<Int, CoverResult>,
    coverLoading: Set<Int>,
    coverError: String?,
    onGenerateAllCovers: (CoverRequest) -> Unit,
    onRegenerateCover: (Int, CoverRequest) -> Unit,
    teaser: TeaserResult?,
    teaserLoadingKind: TeaserLoadingKind?,
    teaserError: String?,
    onTeaserPreview: (String, String) -> Unit,
    onTeaserVideo: (title: String, author: String, lowQuality: Boolean) -> Unit,
    onTeaserRegenerate: (String, String) -> Unit,
    /** Fetches the MP4 via Bearer-auth and triggers a Blob download so
     *  the signed asset URL never appears in the browser history bar. */
    onTeaserDownload: (title: String) -> Unit,
    /** Lifetime free pool remaining for annotation generations. */
    freeAnnotationsRemaining: Int,
    /** Lifetime free pool remaining for full cover-batches (3 covers each). */
    freeCoverBatchesRemaining: Int,
    /** Lifetime free pool remaining for teaser scene previews. */
    freeTeaserPreviewsRemaining: Int,
    /** Current book — used by TeaserPreview to fetch the keyframe / MP4
     *  bytes through the auth-aware Ktor client. Null while no book
     *  is selected (the section is hidden in that state anyway). */
    bookId: String?,
    /** Rendered inside a framed CollapsibleBlock — drop this section's own card
     *  frame + header (chevron/title/status/help); the frame owns them. */
    bare: Boolean = false,
) {
    val s = LocalStrings.current
    val ready = aiState == AnalysisState.Done && aiReport != null

    var expanded by remember { mutableStateOf(true) }

    // Gold accent stripe on the left edge — visual code «это про
    // продажу / конверсия». Promo is the revenue driver, distinguishes
    // it from neutral metrics and brand-violet AI section. padding-left
    // dropped from 20 → 16 so the content stays where it was after the
    // 4px stripe is added.
    Div({
        style {
            if (!bare) {
                bg(WLColors.SurfaceContainerLow)
                property("border-radius", "8px")
                property("padding", "20px 20px 20px 16px")
                property("border-left", "4px solid ${WLColors.Secondary}")
            }
            flexCol(gapPx = 16)
            fillWidth()
            property("box-sizing", "border-box")
        }
    }) {
        // Шапка с заголовком, статусом, chevron'ом и «?»-кнопкой.
        // Кликабельная только зона chevron+заголовок+статус — «?» вынесен
        // в outer-row, чтобы клик по подсказке не сворачивал блок.
        if (!bare) {
        Div({
            style {
                flexRow(gapPx = 12, align = AlignItems.Center)
                fillWidth()
            }
        }) {
            Div({
                style {
                    flexRow(gapPx = 12, align = AlignItems.Center)
                    property("flex", "1")
                    property("min-width", "0")
                    clickableCursor()
                }
                onClick { expanded = !expanded }
            }) {
                // Chevron — текстом, чтобы не тянуть icon-зависимости
                Span({
                    style {
                        fg(WLColors.Secondary)
                        typography(TextLevel.TitleMedium)
                        property("width", "16px")
                        property("flex-shrink", "0")
                    }
                }) { Text(if (expanded) "▾" else "▸") }

                // Заголовок — gold для соответствия золотой левой полосе.
                Span({
                    style {
                        typography(TextLevel.HeadlineSmall)
                        fg(WLColors.Secondary)
                    }
                }) { Text(s.promoMaterialsTitle) }

                // Статус справа — пилюля заметнее голого текста.
                val statusBg = if (ready) "rgba(111, 207, 151, 0.15)" else "rgba(123, 118, 130, 0.15)"
                val statusFg = if (ready) WLColors.Success else WLColors.OnSurfaceVariant
                Span({
                    style {
                        typography(TextLevel.LabelSmall)
                        fg(statusFg)
                        property("background-color", statusBg)
                        property("padding", "4px 12px")
                        property("border-radius", "120px")
                        property("white-space", "nowrap")
                        property("margin-left", "auto")
                    }
                }) {
                    Text(if (ready) s.promoStatusReady else s.promoStatusLocked)
                }
            }
            BlockHelp(s.helpPromoMaterialsTitle, s.helpPromoMaterialsBody, BlockKind.AI, id = AnalyticsEvents.HelpBlock.PROMO_MATERIALS)
        }
        }

        // Контент раскрывается когда expanded (или всегда во framed-режиме).
        if (bare || expanded) {
            Div({
                classes("wl-fade-in")
                style {
                    flexCol(gapPx = 12)
                    fillWidth()
                }
            }) {
                Span({
                    style {
                        typography(TextLevel.BodySmall)
                        fg(if (ready) WLColors.OnSurfaceVariant else WLColors.Outline)
                    }
                }) {
                    Text(if (ready) s.promoMaterialsHintReady else s.promoMaterialsHintLocked)
                }

                PromoTabs(
                    enabled = ready,
                    annotation = annotation,
                    annotationLoading = annotationLoading,
                    annotationError = annotationError,
                    onGenerateAnnotation = onGenerateAnnotation,
                    covers = covers,
                    coverLoading = coverLoading,
                    coverError = coverError,
                    onGenerateAllCovers = onGenerateAllCovers,
                    onRegenerateCover = onRegenerateCover,
                    teaser = teaser,
                    teaserLoadingKind = teaserLoadingKind,
                    teaserError = teaserError,
                    onTeaserPreview = onTeaserPreview,
                    onTeaserVideo = onTeaserVideo,
                    onTeaserRegenerate = onTeaserRegenerate,
                    onTeaserDownload = onTeaserDownload,
                    freeAnnotationsRemaining = freeAnnotationsRemaining,
                    freeCoverBatchesRemaining = freeCoverBatchesRemaining,
                    freeTeaserPreviewsRemaining = freeTeaserPreviewsRemaining,
                    bookId = bookId,
                )
            }
        }
    }
}

/**
 * Tab strip + per-tab body. Все три (annotation / cover / video) —
 * real-генерация, state приходит сверху из VM. Selected-tab держим
 * локально, чтобы переключение между вкладками не сбрасывало форму
 * или показ результата.
 */
@Composable
private fun PromoTabs(
    enabled: Boolean,
    annotation: AnnotationResult?,
    annotationLoading: Boolean,
    annotationError: String?,
    onGenerateAnnotation: () -> Unit,
    covers: Map<Int, CoverResult>,
    coverLoading: Set<Int>,
    coverError: String?,
    onGenerateAllCovers: (CoverRequest) -> Unit,
    onRegenerateCover: (Int, CoverRequest) -> Unit,
    teaser: TeaserResult?,
    teaserLoadingKind: TeaserLoadingKind?,
    teaserError: String?,
    onTeaserPreview: (String, String) -> Unit,
    onTeaserVideo: (title: String, author: String, lowQuality: Boolean) -> Unit,
    onTeaserRegenerate: (String, String) -> Unit,
    onTeaserDownload: (String) -> Unit,
    freeAnnotationsRemaining: Int,
    freeCoverBatchesRemaining: Int,
    freeTeaserPreviewsRemaining: Int,
    bookId: String?,
) {
    val s = LocalStrings.current
    var selectedTab by remember { mutableStateOf(PromoTab.Cover) }

    // Title + author have moved to the sidebar's PromoSettingsCard —
    // two full-width text fields used to sit above the tab strip in
    // the centre of the page, eating vertical space even on the
    // annotation tab (which doesn't use them). Shared holder lives
    // in LocalPromoSettings (provided at ResultsArea level, reset on
    // book switch via remember(bookId)).
    val promoSettings = LocalPromoSettings.current
    val sharedTitle = promoSettings.title
    val sharedAuthor = promoSettings.author

    // Tab strip — no wrapping box. The strip sits directly on the
    // outer Promo section surface; the gold underline on the selected
    // tab is the only selection cue needed. Wrapping was visually a
    // no-op (same colour as outer) but added a nested-card feel.
    Div({
        style {
            fillWidth()
            property("box-sizing", "border-box")
            property("overflow-x", "auto")
        }
    }) {
        Div({
            style {
                flexRow(gapPx = 2)
                property("min-width", "max-content")
                property("border-bottom", "1px solid ${WLColors.OutlineVariant}")
            }
        }) {
            PromoTab.entries.forEach { tab ->
                val isSelected = tab == selectedTab
                // Per-tab status: generated → green check, loading →
                // pulsing dot, otherwise nothing. Lets the user see at
                // a glance which artefacts are done without flipping
                // through tabs.
                val status: TabStatus = when (tab) {
                    PromoTab.Annotation -> when {
                        annotationLoading -> TabStatus.LOADING
                        annotation != null -> TabStatus.DONE
                        else -> TabStatus.NONE
                    }
                    PromoTab.Cover -> when {
                        coverLoading.isNotEmpty() -> TabStatus.LOADING
                        covers.isNotEmpty() -> TabStatus.DONE
                        else -> TabStatus.NONE
                    }
                    PromoTab.Video -> when {
                        teaserLoadingKind != null -> TabStatus.LOADING
                        teaser != null && (teaser.video_exists || teaser.frame_exists) -> TabStatus.DONE
                        else -> TabStatus.NONE
                    }
                }
                // Active tab — gold-coloured text + 2px gold underline.
                // No background fill: the underline reads as a tighter,
                // less «button-like» selection state and visually echoes
                // the gold accent stripe on the section.
                Div({
                    style {
                        property("padding", "10px 14px 8px 14px")
                        typography(TextLevel.LabelLarge)
                        property("white-space", "nowrap")
                        property(
                            "border-bottom",
                            if (isSelected) "2px solid ${WLColors.Secondary}"
                            else "2px solid transparent"
                        )
                        fg(if (isSelected) WLColors.Secondary else WLColors.OnSurfaceVariant)
                        flexRow(gapPx = 6, align = AlignItems.Center)
                        clickableCursor()
                    }
                    onClick { selectedTab = tab }
                }) {
                    Text(tab.title(s))
                    TabStatusDot(status)
                }
            }
        }
    }

    androidx.compose.runtime.key(selectedTab) {
        // Tab content shares the section's surface (no colour shift) but
        // gets a 1px outline so controls inside have a clear visual
        // anchor — without it, sub-toggles and labels float against an
        // empty background and read as «something is broken». The
        // border defines the group without the «card-on-card light
        // panel» feel of the previous SurfaceContainerHigh wrapper.
        Div({
            classes("wl-fade-in")
            style {
                fillWidth()
                property("box-sizing", "border-box")
                property("padding", "16px")
                property("border", "1px solid ${WLColors.OutlineVariant}")
                property("border-radius", "8px")
                flexCol(gapPx = 12)
            }
        }) {
            when (selectedTab) {
                PromoTab.Annotation -> AnnotationTabBody(
                    enabled = enabled,
                    annotation = annotation,
                    loading = annotationLoading,
                    error = annotationError,
                    onGenerate = onGenerateAnnotation,
                    bookTitle = sharedTitle,
                    freeAnnotationsRemaining = freeAnnotationsRemaining,
                )
                PromoTab.Cover -> CoverTabBody(
                    enabled = enabled,
                    covers = covers,
                    loading = coverLoading,
                    error = coverError,
                    onGenerateAll = onGenerateAllCovers,
                    onRegenerate = onRegenerateCover,
                    title = sharedTitle,
                    author = sharedAuthor,
                    onMissingTitle = { promoSettings.titleHighlightTick++ },
                    freeBatchesRemaining = freeCoverBatchesRemaining,
                )
                PromoTab.Video -> TeaserTabBody(
                    enabled = enabled,
                    teaser = teaser,
                    loadingKind = teaserLoadingKind,
                    error = teaserError,
                    onPreview = onTeaserPreview,
                    onVideo = onTeaserVideo,
                    onRegenerate = onTeaserRegenerate,
                    onDownload = onTeaserDownload,
                    title = sharedTitle,
                    author = sharedAuthor,
                    onMissingTitle = { promoSettings.titleHighlightTick++ },
                    freePreviewsRemaining = freeTeaserPreviewsRemaining,
                    bookId = bookId,
                )
            }
        }
    }
}

/**
 * Annotation tab body. Source of truth is the VM — three params
 * (annotation, loading, error) drive Idle/Loading/Error/Generated
 * presentation without a local state machine.
 */
@Composable
private fun AnnotationTabBody(
    enabled: Boolean,
    annotation: AnnotationResult?,
    loading: Boolean,
    error: String?,
    onGenerate: () -> Unit,
    bookTitle: String,
    freeAnnotationsRemaining: Int,
) {
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    val analytics = LocalAnalyticsEvents.current
    // First-time annotation is free if user still has free-annotation
    // quota; subsequent regenerations are always paid (annotation
    // free-quota is "try it once" only — once they generate, the
    // counter is gone whether they regen or not).
    val annotationCost = if (freeAnnotationsRemaining > 0) 0
                         else pricing.annotationCost
    // selectedHook хостим здесь, чтобы Download-кнопка ниже могла
    // прочитать его и собрать .md с актуально выбранным вариантом.
    // remember(annotation) сбрасывает индекс при регенерации.
    var selectedHook by remember(annotation) { mutableStateOf(0) }
    when {
        loading -> LoadingRow()
        annotation != null -> {
            Div({ classes("wl-fade-in") }) {
                AnnotationPreview(
                    result = annotation,
                    selectedHook = selectedHook,
                    onSelectHook = { selectedHook = it },
                )
            }
            // Download + Regenerate — Download outlined-second, Regenerate
            // filled-primary, full-width столбцом (тот же приём что у Cover).
            Div({
                style {
                    flexCol(gapPx = 8)
                    fillWidth()
                }
            }) {
                OutlinedButton(
                    label = s.promoAnnotationDownloadButton,
                    onClick = {
                        analytics.promoDownload(AnalyticsEvents.PromoKind.ANNOTATION, format = "md")
                        downloadAnnotationMd(
                            annotation = annotation,
                            selectedHookIndex = selectedHook,
                            bookTitle = bookTitle,
                        )
                    },
                )
                GenerateButton(
                    // Regen always charges full price — free-quota was
                    // consumed on the initial generation. Hardcoded
                    // pricing.annotationCost (no quota check).
                    label = s.promoRegenerateButton,
                    priceAmount = pricing.annotationCost,
                    enabled = true,
                    onClick = onGenerate,
                )
            }
            if (error != null) ErrorLine(error)
        }
        else -> {
            AnnotationPlaceholder()
            GenerateButton(
                label = s.promoGenerateButton,
                priceAmount = annotationCost,
                enabled = enabled,
                onClick = onGenerate,
            )
            FreeQuotaHint(
                remaining = freeAnnotationsRemaining,
                total = pricing.freeAnnotationsPerUser,
                hintKind = FreeQuotaKind.ANNOTATION,
            )
            if (error != null) ErrorLine(error)
        }
    }
}

/**
 * Cover tab body. Form (title required, author optional) plus three
 * slots — one per variant — each with its own state and regen button.
 * The master button at the bottom regenerates prompts + all three
 * covers; per-slot buttons reuse the existing prompts and only refresh
 * that slot.
 *
 * Each slot is independent: variant 2 can be loading while variants
 * 1 and 3 are visible.
 */
@Composable
private fun CoverTabBody(
    enabled: Boolean,
    covers: Map<Int, CoverResult>,
    loading: Set<Int>,
    error: String?,
    onGenerateAll: (CoverRequest) -> Unit,
    onRegenerate: (Int, CoverRequest) -> Unit,
    title: String,
    author: String,
    onMissingTitle: () -> Unit,
    freeBatchesRemaining: Int,
) {
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    val analytics = LocalAnalyticsEvents.current
    var textMode by remember { mutableStateOf("baked") }

    val titleTrim = title.trim()
    val anyLoading = loading.isNotEmpty()
    val canSubmit = enabled && titleTrim.isNotEmpty() && !anyLoading
    val hasAny = covers.isNotEmpty()
    // `enabled` from the parent already encodes "AI analysis done"; if
    // it's true and we still can't submit, the blocker is the empty
    // title field, not the AI report. Pick the message accordingly so
    // the disabled-button hint isn't actively misleading.
    val disabledHint = if (!enabled) s.promoMaterialsHintLocked
    else s.promoCoverTitleRequired
    // Если кнопку нажали в disabled-режиме и блокер именно «пустое
    // название» — бампаем titleHighlightTick в PromoTabs, чтобы поле
    // ввода вспыхнуло красным.
    val flashTitleIfMissing = {
        if (enabled && titleTrim.isEmpty()) onMissingTitle()
    }
    // Batch vs per-slot pricing now lives on the endpoint, not on a
    // request flag — каждая ручка сама решает сколько списывать.
    val req = {
        CoverRequest(
            title = titleTrim,
            author = author.trim(),
            textMode = textMode,
        )
    }

    CoverTextModeToggle(value = textMode, onChange = { textMode = it })

    val fileBase = coverFileBase(titleTrim)
    val downloadName: (Int, CoverResult) -> String = { v, cv ->
        val ext = if (cv.format == "png") "png" else "svg"
        "${fileBase}_v${v}.$ext"
    }

    // 3-слот-сетка рисуется всегда — в idle CoverSlot показывает пустую
    // плитку 2:3 как placeholder, чтобы пользователь видел будущую раскладку
    // обложек ещё до генерации. На узком экране `.wl-cover-grid` ниже
    // в CSS превращается в горизонтальную галерею со скроллом, чтобы
    // обложки не вытягивались в столбик во весь экран.
    Div({
        classes("wl-cover-grid")
        style {
            fillWidth()
            property("padding-top", "6px")
        }
    }) {
        for (v in 1..3) {
            val cv = covers[v]
            CoverSlot(
                variant = v,
                cover = cv,
                loading = v in loading,
                canRegen = canSubmit,
                onRegen = { onRegenerate(v, req()) },
                onDownload = {
                    if (cv != null) {
                        analytics.promoDownload(AnalyticsEvents.PromoKind.COVER, format = cv.format)
                        downloadCover(cv, downloadName(v, cv))
                    }
                },
            )
        }
    }

    // Master CTAs: download-all (when any cover is ready) above the
    // primary generate / regenerate-all action, stacked full-width.
    // Download-all is hidden in the empty state so the only CTA is the
    // generate action. While any slot is generating we replace the
    // whole block with a LoadingRow — same pattern as the annotation
    // tab, so the user sees one consistent "generation in flight" cue
    // instead of a CTA that silently no-ops on click.
    when {
        anyLoading -> LoadingRow()
        hasAny -> Div({
            style {
                flexCol(gapPx = 8)
                fillWidth()
            }
        }) {
            // Outlined "Download all", full-width. Sequential downloads
            // with a small delay each so the browser handles them as
            // separate user-initiated saves rather than collapsing them
            // into one.
            Div({
                style {
                    typography(TextLevel.LabelLarge)
                    property("padding", "10px 16px")
                    property("border-radius", "120px")
                    property("text-align", "center")
                    property("box-sizing", "border-box")
                    border("1px", "solid", WLColors.Secondary)
                    fg(WLColors.Secondary)
                    clickableCursor()
                    fillWidth()
                }
                onClick {
                    val ordered = covers.keys.sorted()
                    var idx = 0
                    for (v in ordered) {
                        if (v in loading) continue
                        val cv = covers[v] ?: continue
                        val delayMs = idx * 250
                        analytics.promoDownload(AnalyticsEvents.PromoKind.COVER, format = cv.format)
                        kotlinx.browser.window.setTimeout(
                            { downloadCover(cv, downloadName(v, cv)); Unit },
                            delayMs,
                        )
                        idx++
                    }
                }
            }) { Text(s.promoCoverDownloadAll) }

            GenerateButton(
                // Regen-all = 3 sequential calls × coverRegenCost each.
                label = s.promoCoverRegenerateAll,
                priceAmount = 3 * pricing.coverRegenCost,
                enabled = canSubmit,
                onClick = { onGenerateAll(req()) },
                disabledHint = disabledHint,
                onDisabledClick = flashTitleIfMissing,
            )
        }
        else -> {
            // Empty state — first generation of the cover set. Free if
            // user still has free-batch quota; otherwise 3 cr upfront.
            val initialBatchCost = if (freeBatchesRemaining > 0) 0
                                   else 3 * pricing.coverRegenCost
            GenerateButton(
                label = s.promoCoverGenerateAll,
                priceAmount = initialBatchCost,
                enabled = canSubmit,
                onClick = { onGenerateAll(req()) },
                disabledHint = disabledHint,
                onDisabledClick = flashTitleIfMissing,
            )
            // Quota-state hint under the CTA: surfaces "N of M free
            // sets remaining" while quota lasts, and "all N free sets
            // used" the moment it's exhausted. Helps the user
            // understand why the next click suddenly costs money.
            FreeQuotaHint(
                remaining = freeBatchesRemaining,
                total = pricing.freeCoverBatchesPerUser,
                hintKind = FreeQuotaKind.COVER,
            )
        }
    }
    if (error != null) ErrorLine(error)
}

/** Single cover slot: shows SVG when ready, spinner when loading,
 *  empty placeholder otherwise. Per-slot regen + download buttons
 *  appear only when a cover is present (no point in either action
 *  for an empty slot — the master button generates from scratch). */
@Composable
private fun CoverSlot(
    variant: Int,
    cover: CoverResult?,
    loading: Boolean,
    canRegen: Boolean,
    onRegen: () -> Unit,
    onDownload: () -> Unit,
) {
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    Div({
        style {
            flexCol(gapPx = 6)
            fillWidth()
            box(bgColor = WLColors.SurfaceContainerHigh, radiusPx = 6, paddingPx = 8)
            property("box-sizing", "border-box")
        }
    }) {
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text("${s.promoCoverVariantPrefix} $variant") }

        when {
            loading -> Div({
                style {
                    fillWidth()
                    property("aspect-ratio", "2 / 3")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    bg(WLColors.SurfaceContainerHigh)
                    property("border-radius", "4px")
                }
            }) { MdCircularProgress() }
            cover != null -> Div({
                style {
                    fillWidth()
                    clickableCursor()
                }
                onClick { openCoverLightbox(cover) }
            }) { CoverPreview(cover) }
            else -> Div({
                style {
                    fillWidth()
                    property("aspect-ratio", "2 / 3")
                    bg(WLColors.SurfaceContainerHigh)
                    property("border-radius", "4px")
                    property("overflow", "hidden")
                }
            }) {
                Img(src = "placeholders/cover_$variant.png", alt = "") {
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("display", "block")
                    }
                }
            }
        }

        if (cover != null && !loading) {
            // Download is unconditional — the cover is on the client
            // already, no preconditions to check. Regenerate is always
            // clickable: if its preconditions aren't met (empty title,
            // another regen in-flight) the VM rejects with a message
            // surfaced via the shared ErrorLine below.
            Div({
                style {
                    flexRow(gapPx = 8)
                    property("justify-content", "center")
                    fillWidth()
                }
            }) {
                CoverSlotIconButton(
                    tooltip = s.promoCoverDownloadOne,
                    svgInner = ICON_DOWNLOAD,
                    onClick = onDownload,
                )
                CoverSlotIconButton(
                    tooltip = s.promoCoverRegenerateOne + s.priceTag(pricing.coverRegenCost),
                    svgInner = ICON_REFRESH,
                    onClick = onRegen,
                )
            }
        }
    }
}

/** Round outlined icon button with a tooltip on hover / focus. Inline
 *  SVG so we can drive stroke colour from our token palette without an
 *  icon font dependency. */
@Composable
private fun CoverSlotIconButton(
    tooltip: String,
    svgInner: String,
    onClick: () -> Unit,
) {
    Div({
        classes("wl-tip-host")
        attr("tabindex", "0")
        attr("aria-label", tooltip)
        style {
            property("width", "32px")
            property("height", "32px")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            property("border-radius", "50%")
            border("1px", "solid", WLColors.Secondary)
            clickableCursor()
            property("box-sizing", "border-box")
            property("flex", "0 0 auto")
        }
        onClick { onClick() }
    }) {
        Div({
            style {
                property("width", "18px")
                property("height", "18px")
                property("display", "flex")
                // pointer-events: none lets the host's click + hover work
                // even when the cursor is over the SVG glyph itself.
                property("pointer-events", "none")
            }
            ref { node ->
                node.innerHTML =
                    """<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                       viewBox="0 0 24 24" fill="none" stroke="${WLColors.Secondary}"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                       $svgInner
                    </svg>"""
                onDispose { node.innerHTML = "" }
            }
        })
        TooltipBubble(tooltip)
    }
}

// Feather-style glyphs (24×24, stroke-only) for slot actions.
private const val ICON_DOWNLOAD =
    """<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
       <polyline points="7 10 12 15 17 10"/>
       <line x1="12" y1="15" x2="12" y2="3"/>"""
private const val ICON_REFRESH =
    """<polyline points="23 4 23 10 17 10"/>
       <polyline points="1 20 1 14 7 14"/>
       <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>"""

/**
 * Render a cover preview — either the inline SVG (overlay mode) or
 * a `<img>` tag with a base64 PNG data URL (baked mode). SVG variant
 * strips the root tag's fixed pixel dimensions so it scales to the
 * wrapper instead of blowing past max-width; viewBox keeps aspect.
 */
@Composable
private fun CoverPreview(cover: CoverResult) {
    Div({
        style {
            fillWidth()
            property("display", "flex")
            property("justify-content", "center")
        }
    }) {
        if (cover.format == "png") {
            Img(src = "data:image/png;base64,${cover.image}", alt = "cover") {
                attr("style",
                    "max-width:100%;height:auto;display:block;border-radius:4px;"
                )
            }
        } else {
            Div({
                style {
                    fillWidth()
                }
                ref { node ->
                    node.innerHTML = cover.image
                    val svgEl = node.asDynamic().querySelector("svg")
                    if (svgEl != null) {
                        svgEl.removeAttribute("width")
                        svgEl.removeAttribute("height")
                        svgEl.setAttribute("style",
                            "width:100%;height:auto;display:block;"
                        )
                    }
                    onDispose { node.innerHTML = "" }
                }
            })
        }
    }
}

// Triggers a download of a single cover via a transient `<a download>`.
// PNG: keep the base64 payload as a data URL; SVG: percent-encode the
// raw XML so `#`, `<`, `>` etc. survive transport. The anchor is appended
// to body for Firefox (which ignores .click() on detached nodes) and
// removed straight after.
private fun downloadCover(cover: CoverResult, filename: String) {
    val href = if (cover.format == "png") {
        "data:image/png;base64,${cover.image}"
    } else {
        "data:image/svg+xml;charset=utf-8," + encodeURIComponent(cover.image)
    }
    val a = kotlinx.browser.document.createElement("a")
        as org.w3c.dom.HTMLAnchorElement
    a.href = href
    a.setAttribute("download", filename)
    a.style.display = "none"
    kotlinx.browser.document.body?.appendChild(a)
    a.click()
    kotlinx.browser.document.body?.removeChild(a)
}

// Filename base built from the book title — strips characters that
// confuse Windows/macOS filesystems and collapses whitespace. Falls
// back to "cover" when the title is blank.
private fun coverFileBase(title: String): String =
    fileBaseFromTitle(title, fallback = "cover")

// Generic FS-safe slug from an arbitrary title. Used by both cover
// downloads and the annotation .md download.
private fun fileBaseFromTitle(title: String, fallback: String): String {
    val cleaned = title.trim()
        .replace(Regex("""[\\/:*?"<>|]+"""), "")
        .replace(Regex("""\s+"""), "_")
    return cleaned.ifEmpty { fallback }
}

// Triggers a download of the annotation as a Markdown file. The chosen
// hook (selectedHookIndex) becomes the bold opening line; the body
// follows after a blank line. Same transient <a download> trick we
// use for covers — keeps payload purely client-side, no backend round
// trip.
private fun downloadAnnotationMd(
    annotation: AnnotationResult,
    selectedHookIndex: Int,
    bookTitle: String,
) {
    val hooks = annotation.hooks
    val body = annotation.annotation.trim()
    val safeIndex = selectedHookIndex.coerceIn(0, (hooks.size - 1).coerceAtLeast(0))
    val hook = if (hooks.isNotEmpty()) hooks[safeIndex].trim() else ""

    val sb = StringBuilder()
    if (hook.isNotEmpty()) {
        sb.append("**").append(hook).append("**\n\n")
    }
    if (body.isNotEmpty()) {
        sb.append(body).append('\n')
    }
    val md = sb.toString()

    val href = "data:text/markdown;charset=utf-8," + encodeURIComponent(md)
    val filename = fileBaseFromTitle(bookTitle, fallback = "annotation") + ".md"

    val a = kotlinx.browser.document.createElement("a")
        as org.w3c.dom.HTMLAnchorElement
    a.href = href
    a.setAttribute("download", filename)
    a.style.display = "none"
    kotlinx.browser.document.body?.appendChild(a)
    a.click()
    kotlinx.browser.document.body?.removeChild(a)
}

// Outlined-secondary pill button — same shape as the filled
// GenerateButton but with a transparent fill + colored outline.
// Used for secondary actions (Download) next to a primary CTA.
@Composable
private fun OutlinedButton(label: String, onClick: () -> Unit) {
    Div({
        style {
            typography(TextLevel.LabelLarge)
            property("padding", "10px 16px")
            property("border-radius", "120px")
            property("text-align", "center")
            property("box-sizing", "border-box")
            border("1px", "solid", WLColors.Secondary)
            fg(WLColors.Secondary)
            clickableCursor()
            fillWidth()
        }
        onClick { onClick() }
    }) { Text(label) }
}

// Opens the cover in a PhotoSwipe lightbox (top-layer modal with built-in
// pinch-to-zoom, swipe-to-close, ESC, scrim, focus trap). Delegates to a
// vanilla-JS shim in `photoswipe-loader.js`; here we just shape the data
// URL and call through.
//
// Why this isn't a `@Composable` overlay: rendering a Compose `position:
// fixed` div inside the section tree is fragile — any ancestor with a
// `transform`/`filter` re-anchors fixed children to itself, producing the
// "scrim larger than the viewport" bug we hit on the first attempt. The
// browser's top layer (which PhotoSwipe uses internally) is immune to that.
private fun openCoverLightbox(cover: CoverResult) {
    val src = if (cover.format == "png") {
        "data:image/png;base64,${cover.image}"
    } else {
        // SVGs from the backend may contain `#` (colors) and other chars
        // that aren't safe in a data URL — encodeURIComponent handles all
        // of them. Cheaper than base64 and keeps the payload smaller.
        "data:image/svg+xml;utf8," + encodeURIComponent(cover.image)
    }
    // Covers are rendered at a 2:3 ratio; the exact pixel size doesn't
    // matter to PhotoSwipe — only the ratio, which it uses to fit the
    // image into the viewport.
    val open = kotlinx.browser.window.asDynamic().__wlOpenLightbox
    open(src, 1024, 1536, "cover")
}

/** Two-button toggle: vector overlay vs. text baked into the image. */
@Composable
private fun CoverTextModeToggle(value: String, onChange: (String) -> Unit) {
    val s = LocalStrings.current
    Div({
        style {
            flexCol(gapPx = 4)
            fillWidth()
        }
    }) {
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(s.promoCoverTextModeLabel) }
        // Toggle group reads as a single segmented control via the 1px
        // outline. Bg stays on the section's surface so the toggle
        // doesn't introduce another light-on-dark layer; the selected
        // segment is the only thing that picks up a different tone.
        Div({
            style {
                property("padding", "3px")
                property("border-radius", "6px")
                property("border", "1px solid ${WLColors.OutlineVariant}")
                flexRow(gapPx = 2)
                fillWidth()
                property("box-sizing", "border-box")
            }
        }) {
            for ((key, label) in listOf(
                "overlay" to s.promoCoverTextModeOverlay,
                "baked" to s.promoCoverTextModeBaked,
            )) {
                val selected = key == value
                Div({
                    style {
                        property("flex", "1")
                        property("padding", "8px 10px")
                        property("border-radius", "4px")
                        property("text-align", "center")
                        typography(TextLevel.LabelSmall)
                        if (selected) {
                            bg(WLColors.SurfaceContainerHigh)
                            fg(WLColors.Primary)
                        } else {
                            fg(WLColors.OnSurfaceVariant)
                        }
                        clickableCursor()
                    }
                    onClick { onChange(key) }
                }) { Text(label) }
            }
        }
    }
}

/** Plain text-input row used inside the Cover form. `highlightTick`
 *  — счётчик, который parent бампает чтобы попросить input «вспыхнуть
 *  красным» (например при попытке генерации без заполненного поля).
 *  Подсветка держится 2.5с и гаснет — короткий visual cue. */
@Composable
private fun CoverInput(
    label: String,
    value: String,
    onChange: (String) -> Unit,
    highlightTick: Int = 0,
    /** Backend-enforced char cap mirrored here as the `maxlength`
     *  attribute (hard browser-side cut-off) plus a small counter
     *  next to the label so the limit isn't a surprise. Null = no
     *  cap (the input is unbounded, no counter shown). */
    maxLength: Int? = null,
) {
    var highlighted by remember { mutableStateOf(false) }
    LaunchedEffect(highlightTick) {
        if (highlightTick > 0) {
            highlighted = true
            delay(2500)
            highlighted = false
        }
    }
    val borderColor = if (highlighted) WLColors.Error else WLColors.OutlineVariant
    val borderWidth = if (highlighted) "2px" else "1px"
    Div({
        style {
            flexCol(gapPx = 4)
            fillWidth()
        }
    }) {
        Div({
            style {
                flexRow(gapPx = 8, align = AlignItems.Baseline)
                fillWidth()
            }
        }) {
            Span({
                style {
                    typography(TextLevel.LabelSmall)
                    fg(if (highlighted) WLColors.Error else WLColors.OnSurfaceVariant)
                    property("flex", "1")
                }
            }) { Text(label) }
            if (maxLength != null) {
                // Pale until the user is well into the field, amber
                // near the cap, error at the cap. Browser maxlength
                // hard-caps typing/paste, so «over» is impossible —
                // this is purely an early-warning visual.
                val counterColor = when {
                    value.length >= maxLength -> WLColors.Error
                    value.length >= (maxLength * 0.9).toInt() -> WLColors.Tertiary
                    else -> WLColors.OnSurfaceVariant
                }
                Span({
                    style {
                        typography(TextLevel.LabelSmall)
                        fg(counterColor)
                    }
                }) { Text("${value.length} / $maxLength") }
            }
        }
        org.jetbrains.compose.web.dom.Input(
            type = org.jetbrains.compose.web.attributes.InputType.Text,
        ) {
            value(value)
            onInput { ev -> onChange(ev.value) }
            if (maxLength != null) attr("maxlength", maxLength.toString())
            attr("style",
                "width:100%;padding:8px 12px;border-radius:6px;" +
                "background:${WLColors.SurfaceContainerLow};" +
                "color:${WLColors.OnSurface};" +
                "border:$borderWidth solid $borderColor;" +
                "font-size:14px;box-sizing:border-box;" +
                "transition:border-color 0.15s, color 0.15s;"
            )
        }
    }
}

/**
 * Teaser tab body. Two-step UX:
 *   1. Idle → click "Generate teaser" → Sonnet + Gemini run for
 *      ~30s and ~$0.04, producing a keyframe + chosen scene.
 *   2. Preview shown → user chooses:
 *      • "Generate video" — Kling i2v adds motion (~2 min, ~$0.56)
 *      • "Pick a different scene" — re-runs from step 1 with fresh
 *        Sonnet call so a new scene/element may be chosen
 *   3. Video shown → "Download video" + "Pick a different scene"
 *
 * Author is optional; title is required. Style reference is auto-
 * picked by the LLM from style_references/.
 */
@Composable
private fun TeaserTabBody(
    enabled: Boolean,
    teaser: TeaserResult?,
    loadingKind: TeaserLoadingKind?,
    error: String?,
    onPreview: (String, String) -> Unit,
    onVideo: (title: String, author: String, lowQuality: Boolean) -> Unit,
    onRegenerate: (String, String) -> Unit,
    onDownload: (String) -> Unit,
    title: String,
    author: String,
    onMissingTitle: () -> Unit,
    freePreviewsRemaining: Int,
    bookId: String?,
) {
    val s = LocalStrings.current

    val pricing = LocalPricing.current
    val hasKeyframe = teaser?.frame_exists == true
    val hasVideo = teaser?.video_exists == true
    val canSubmit = enabled && title.isNotBlank() && loadingKind == null
    // Render-video reuses the existing keyframe (useExistingKeyframe=true
    // server-side). The title overlay is already baked into the saved
    // PNG from preview-time, so title is semantically irrelevant here
    // — user shouldn't have to retype it just to add motion, even
    // after a page reload where the field is empty.
    val canRenderVideo = enabled && hasKeyframe && loadingKind == null
    // Hidden dev shortcut: holding Alt+Shift swaps the render-video
    // CTA into "low-quality" mode — backend routes i2v to the cheap
    // fallback model and charges TEASER_RENDER_LOW_QUALITY_COST
    // instead of TEASER_RENDER_COST. Lets the dev iterate on the
    // pipeline / UI without burning the full vendor cost per click.
    // (Avoid Ctrl+Shift — on macOS Ctrl+click is right-click, the
    // browser swallows the event into a context menu.)
    val lowQualityMode = rememberAltShiftHeld()
    val renderCost = if (lowQualityMode) pricing.teaserRenderLowQualityCost
                     else pricing.teaserRenderCost
    val renderLabel = if (lowQualityMode) "Сгенерировать тест-качество"
                      else s.promoTeaserRenderVideoButton
    // Free-preview quota is per-USER lifetime (not per-book). Frontend
    // mirrors the backend Pricing.teaserCost gate: if user still has
    // any free previews left, this call costs 0; otherwise full price.
    val previewCost = if (freePreviewsRemaining > 0) 0 else pricing.teaserPreviewCost
    // Тот же паттерн что в CoverTabBody: дифференцируем disabled-причину
    // (нет анализа vs пустое название) + бампаем title-highlight tick
    // если блокер именно поле названия.
    val disabledHint = if (!enabled) s.promoMaterialsHintLocked
    else s.promoCoverTitleRequired
    val flashTitleIfMissing = {
        if (enabled && title.isBlank()) onMissingTitle()
    }

    Div({
        style {
            flexCol(gapPx = 12)
            fillWidth()
        }
    }) {
        when {
            // ─── Currently working ───────────────────────────────
            loadingKind != null -> {
                // If we already have a keyframe (e.g. user clicked
                // "Generate video" on a preview), keep showing it
                // alongside the spinner so the user has visual
                // continuity for the 2-minute wait.
                if (hasKeyframe) {
                    Div({ classes("wl-fade-in") }) { TeaserPreview(teaser, showVideo = hasVideo, bookId = bookId) }
                }
                TeaserLoadingRow(loadingKind)
            }

            // ─── Video is ready ──────────────────────────────────
            hasVideo -> {
                Div({ classes("wl-fade-in") }) { TeaserPreview(teaser, showVideo = true, bookId = bookId) }
                Div({ style { flexRow(gapPx = 8, align = AlignItems.Center) } }) {
                    GenerateButton(
                        label = s.promoTeaserPickAnotherScene,
                        priceAmount = previewCost,
                        enabled = canSubmit,
                        onClick = { onRegenerate(title, author) },
                        disabledHint = disabledHint,
                        onDisabledClick = flashTitleIfMissing,
                    )
                    GenerateButton(
                        label = s.promoTeaserDownloadButton,
                        enabled = true,
                        onClick = { onDownload(title) },
                    )
                }
                if (error != null) ErrorLine(mapTeaserError(error, s))
            }

            // ─── Preview ready (keyframe only) ───────────────────
            hasKeyframe -> {
                Div({ classes("wl-fade-in") }) { TeaserPreview(teaser, showVideo = false, bookId = bookId) }
                Div({ style { flexRow(gapPx = 8, align = AlignItems.Center) } }) {
                    GenerateButton(
                        label = s.promoTeaserPickAnotherScene,
                        priceAmount = previewCost,
                        enabled = canSubmit,
                        onClick = { onRegenerate(title, author) },
                        disabledHint = disabledHint,
                        onDisabledClick = flashTitleIfMissing,
                    )
                    GenerateButton(
                        label = renderLabel,
                        priceAmount = renderCost,
                        enabled = canRenderVideo,
                        onClick = { onVideo(title, author, lowQualityMode) },
                        disabledHint = disabledHint,
                    )
                }
                if (error != null) ErrorLine(mapTeaserError(error, s))
            }

            // ─── Idle, never generated ───────────────────────────
            else -> {
                TeaserPlaceholder()
                // Двухэтапный explainer: пользователь должен видеть сразу,
                // что процесс делится на «подбор сцены» и «рендер видео»
                // с разной ценой и временем, и понимать общий чек, чтобы
                // не было сюрприза «нажал, потом ещё 4 кр сверху».
                TeaserStepsBreakdown()
                GenerateButton(
                    label = s.promoTeaserPreviewButton,
                    priceAmount = previewCost,
                    enabled = canSubmit,
                    onClick = { onPreview(title, author) },
                    disabledHint = disabledHint,
                    onDisabledClick = flashTitleIfMissing,
                )
                FreeQuotaHint(
                    remaining = freePreviewsRemaining,
                    total = pricing.freeTeaserPreviewsPerUser,
                    hintKind = FreeQuotaKind.TEASER,
                )
                if (error != null) ErrorLine(mapTeaserError(error, s))
            }
        }
    }
}

/** Какую квоту описывает FreeQuotaHint — определяет какие i18n
 *  строки использовать (cover-set или teaser-preview). */
internal enum class FreeQuotaKind { ANNOTATION, COVER, TEASER, HIGHLIGHT }

/**
 * Маленькая подсказка под CTA: «осталось N из M бесплатных»
 * пока квота > 0; «квота исчерпана» когда дошли до нуля. Цвет —
 * успокаивающий зелёный пока бесплатно, нейтрально-серый когда уже
 * платно. Не показывается если бэкенд почему-то прислал total=0.
 */
@Composable
internal fun FreeQuotaHint(remaining: Int, total: Int, hintKind: FreeQuotaKind) {
    if (total <= 0) return
    // The remaining/exhausted count describes a signed-in user's per-account
    // free pool. For anonymous visitors it's meaningless (no account yet →
    // remaining always reads 0 → a misleading "exhausted"), so show nothing
    // until they sign in. Auth transitions are full-page reloads, so reading
    // the flag at composition is safe (it can't flip mid-session).
    if (!Auth.isLoggedIn) return
    val s = LocalStrings.current
    val text = when {
        remaining > 0 && hintKind == FreeQuotaKind.ANNOTATION ->
            s.freeQuotaAnnotationRemaining(remaining, total)
        remaining > 0 && hintKind == FreeQuotaKind.COVER ->
            s.freeQuotaCoverRemaining(remaining, total)
        remaining > 0 && hintKind == FreeQuotaKind.HIGHLIGHT ->
            s.freeQuotaHighlightRemaining(remaining, total)
        remaining > 0 ->
            s.freeQuotaTeaserRemaining(remaining, total)
        hintKind == FreeQuotaKind.ANNOTATION ->
            s.freeQuotaAnnotationExhausted(total)
        hintKind == FreeQuotaKind.COVER ->
            s.freeQuotaCoverExhausted(total)
        hintKind == FreeQuotaKind.HIGHLIGHT ->
            s.freeQuotaHighlightExhausted(total)
        else ->
            s.freeQuotaTeaserExhausted(total)
    }
    val color = if (remaining > 0) WLColors.Tertiary else WLColors.OnSurfaceVariant
    Span({
        style {
            typography(TextLevel.LabelSmall)
            fg(color)
            property("text-align", "center")
            property("padding-top", "2px")
        }
    }) { Text(text) }
}

/**
 * Map a raw backend teaser error to a user-friendly localized message.
 * Currently handles the Kling copyright-filter rejection (the most
 * common production error on famous IP titles); everything else falls
 * through unchanged. Substring-matched on the upstream Kling text —
 * if the wording changes server-side we'll need a structured error
 * code, but until then this keeps the fix local to the frontend.
 */
private fun mapTeaserError(raw: String, s: Strings): String = when {
    raw.contains("copyright", ignoreCase = true) -> s.promoTeaserErrorCopyright
    // i2v vendors occasionally refuse keyframes that "may contain
    // a real person" (privacy filter on input image). Surface as
    // a clear hint instead of leaking the raw nested vendor error.
    raw.contains("real person", ignoreCase = true) ||
        raw.contains("InputImageSensitiveContent", ignoreCase = true) ->
        s.promoTeaserErrorPrivacyFace
    else -> raw
}

/** Loading row with phase-specific copy (i18n). */
@Composable
private fun TeaserLoadingRow(kind: TeaserLoadingKind) {
    val s = LocalStrings.current
    val copy = when (kind) {
        TeaserLoadingKind.Preview -> s.promoTeaserLoadingPreview
        TeaserLoadingKind.Video -> s.promoTeaserLoadingVideo
        TeaserLoadingKind.Regenerate -> s.promoTeaserLoadingRegenerate
    }
    Div({
        style {
            flexRow(gapPx = 10, align = AlignItems.Center)
            property("padding", "12px 0")
        }
    }) {
        MdCircularProgress()
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(copy) }
    }
}

/**
 * Track whether Alt AND Shift are both held right now. Window-level
 * keydown/keyup listeners drive a boolean state; a blur fallback
 * resets it (otherwise alt-tabbing out while the keys are down would
 * leave the UI stuck in the debug variant).
 *
 * Used to surface the hidden low-quality teaser-render shortcut: when
 * the user holds Alt+Shift, the "render video" button morphs into a
 * cheap-model debug variant priced at 1 credit. No discovery hint —
 * intentional, this is a developer affordance not a user feature.
 *
 * Why Alt+Shift and not Ctrl+Shift: macOS treats Ctrl+click as
 * right-click and routes the event to the context menu instead of
 * the button's onClick — so Ctrl+Shift+click never fires here.
 * Alt+Shift is unbound on both macOS and Windows for click events.
 */
@Composable
internal fun rememberAltShiftHeld(): Boolean {
    var held by remember { mutableStateOf(false) }
    DisposableEffect(Unit) {
        val onKey: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val ke = ev.unsafeCast<org.w3c.dom.events.KeyboardEvent>()
            held = ke.altKey && ke.shiftKey
        }
        val onBlur: (org.w3c.dom.events.Event) -> Unit = { held = false }
        kotlinx.browser.window.addEventListener("keydown", onKey)
        kotlinx.browser.window.addEventListener("keyup", onKey)
        kotlinx.browser.window.addEventListener("blur", onBlur)
        onDispose {
            kotlinx.browser.window.removeEventListener("keydown", onKey)
            kotlinx.browser.window.removeEventListener("keyup", onKey)
            kotlinx.browser.window.removeEventListener("blur", onBlur)
        }
    }
    return held
}

/**
 * Fetch a binary payload via Bearer-authed `fetch`, wrap it in a Blob,
 * and return the corresponding object URL. `null` while the bytes are
 * loading or when `key` itself is null (caller signalling "don't
 * fetch"). The blob URL is revoked on dispose AND whenever `key`
 * changes — preventing the per-asset memory leak that would otherwise
 * accrue across regenerations.
 *
 * The key doubles as a fetch trigger: pass null to disable, pass any
 * structural value (string, data class, …) to (re)trigger. State is
 * reset when the key changes so a stale blob URL never lingers for a
 * different asset.
 */
@Composable
private fun rememberBlobUrl(
    key: Any?,
    mime: String,
    fetch: suspend () -> ByteArray?,
): String? {
    val scope = rememberCoroutineScope()
    var url by remember(key) { mutableStateOf<String?>(null) }
    DisposableEffect(key) {
        var created: String? = null
        val job = if (key != null) {
            scope.launch {
                val bytes = fetch() ?: return@launch
                created = createBlobUrl(bytes, mime)
                url = created
            }
        } else null
        onDispose {
            job?.cancel()
            created?.let { revokeBlobUrl(it) }
        }
    }
    return url
}

/**
 * Show the rendered keyframe (or video when ready), plus a compact
 * metadata block (chosen scene element + style reference reason).
 *
 * `showVideo=true` plays the MP4 if present; `showVideo=false` always
 * shows the still keyframe (used while video is being generated so
 * the user has visual continuity).
 */
@Composable
private fun TeaserPreview(
    teaser: TeaserResult,
    showVideo: Boolean = true,
    bookId: String?,
) {
    // Fetch the asset bytes via the Bearer-authed Ktor client and wrap
    // them in a Blob URL. The backend asset URL is never bound to a DOM
    // element — the only thing the page ever sees is the opaque
    // `blob:` identifier. `teaser` is part of the cache key so a fresh
    // generation (new scene / video render) invalidates and refetches.
    val showVideoEffective = showVideo && teaser.video_exists
    val version = teaser.hashCode().toString()
    val videoBlobUrl = rememberBlobUrl(
        key = if (showVideoEffective && bookId != null) "video:$bookId:$version" else null,
        mime = "video/mp4",
    ) { bookId?.let { BookRepository.fetchTeaserVideoBytes(it, version) } }
    val frameBlobUrl = rememberBlobUrl(
        key = if (teaser.frame_exists && bookId != null) "frame:$bookId:$version" else null,
        mime = "image/png",
    ) { bookId?.let { BookRepository.fetchTeaserFrameBytes(it, version) } }

    Div({
        style {
            flexCol(gapPx = 10)
            fillWidth()
        }
    }) {
        if (showVideoEffective && videoBlobUrl != null) {
            Video({
                attr("src", videoBlobUrl)
                attr("controls", "")
                attr("autoplay", "")
                attr("loop", "")
                attr("muted", "")
                attr("playsinline", "")
                style {
                    fillWidth()
                    property("max-height", "420px")
                    property("border-radius", "6px")
                    property("background", "#000")
                }
            })
        } else if (frameBlobUrl != null) {
            Img(src = frameBlobUrl) {
                style {
                    fillWidth()
                    property("max-height", "420px")
                    property("object-fit", "contain")
                    property("border-radius", "6px")
                }
            }
        }

        // Scene metadata only — the style-reasoning block is
        // intentionally omitted (it's plumbing, not user value).
        // Null teaser_prompt happens in partial-failure states where
        // generation never produced teaser_prompt.json — skip the
        // block entirely.
        val s = LocalStrings.current
        val meta = teaser.teaser_prompt
        if (meta != null && meta.chosen_element.isNotBlank()) {
            Div({
                style {
                    box(bgColor = WLColors.SurfaceContainerHigh, radiusPx = 6, paddingPx = 10)
                    flexCol(gapPx = 4)
                }
            }) {
                Span({
                    style {
                        typography(TextLevel.BodySmall)
                        fg(WLColors.OnSurface)
                    }
                }) { Text("${s.promoTeaserSceneLabel}: ${meta.chosen_element}") }
            }
        }
    }
}

/**
 * Idle-плейсхолдер для аннотации. Карточка-объяснение: почему стоит
 * сгенерировать промо-аннотацию, что её делает продающей. Заполняет
 * пустое место смысловой нагрузкой, а не серым прямоугольником.
 */
@Composable
private fun AnnotationPlaceholder() {
    val s = LocalStrings.current
    Div({
        style {
            fillWidth()
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("padding", "28px 24px")
            property("box-sizing", "border-box")
            flexCol(gapPx = 10)
            property("align-items", "center")
        }
    }) {
        Span({
            style {
                typography(TextLevel.TitleSmall)
                fg(WLColors.OnSurface)
                property("font-weight", "600")
                property("text-align", "center")
            }
        }) { Text(s.promoAnnotationPlaceholderHeading) }
        Span({
            style {
                typography(TextLevel.BodySmall)
                fg(WLColors.OnSurfaceVariant)
                property("text-align", "center")
                property("max-width", "560px")
                property("line-height", "1.6")
            }
        }) { Text(s.promoAnnotationPlaceholderBody) }
    }
}

/** Idle-плейсхолдер для видео-тизера: 5-сек ролик через Kling i2v
 *  (autoplay/muted/loop) поверх intro.jpg как poster — мгновенный
 *  визуал на первом кадре, потом cinematic motion заводит пользователя. */
@Composable
private fun TeaserPlaceholder() {
    Div({
        style {
            fillWidth()
            property("aspect-ratio", "16 / 9")
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("overflow", "hidden")
        }
    }) {
        Video({
            attr("src", "placeholders/intro.mp4")
            attr("poster", "placeholders/intro.jpg")
            attr("autoplay", "")
            attr("muted", "")
            attr("loop", "")
            attr("playsinline", "")
            attr("preload", "metadata")
            style {
                property("width", "100%")
                property("height", "100%")
                property("object-fit", "cover")
                property("display", "block")
            }
        })
    }
}

/**
 * Двухэтапный price-breakdown под idle-плейсхолдером тизер-вкладки.
 * Показывает заранее: «шаг 1 — 1 кр / 30 сек, шаг 2 — 4 кр / 1-2 мин,
 * итого 5 кр за полный 5-сек ролик». Без сюрпризов: пользователь
 * нажимает «Подобрать сцену» зная, что после превью его ещё ждёт
 * платёж за рендер.
 */
@Composable
private fun TeaserStepsBreakdown() {
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    Div({
        style {
            fillWidth()
            box(bgColor = WLColors.SurfaceContainerHigh, radiusPx = 6, paddingPx = 12)
            flexCol(gapPx = 8)
            property("box-sizing", "border-box")
        }
    }) {
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(s.promoTeaserStepsHeading) }

        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "1fr auto auto")
                property("column-gap", "12px")
                property("row-gap", "4px")
                property("align-items", "baseline")
                fillWidth()
            }
        }) {
            TeaserStepCells(
                num = 1,
                title = s.promoTeaserStep1Title,
                time = s.promoTeaserStep1Time,
                costLabel = s.promoTeaserStep1Cost(
                    pricing.freeTeaserPreviewsPerUser,
                    pricing.teaserPreviewCost,
                ),
            )
            TeaserStepCells(
                num = 2,
                title = s.promoTeaserStep2Title,
                time = s.promoTeaserStep2Time,
                costLabel = s.priceUnits(pricing.teaserRenderCost),
            )
        }

        Div({
            style {
                property("border-top", "1px solid ${WLColors.OutlineVariant}")
                property("padding-top", "8px")
                flexRow(gapPx = 8, align = AlignItems.Center)
                fillWidth()
            }
        }) {
            Span({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurface)
                    property("flex", "1")
                }
            }) { Text(s.promoTeaserStepsTotalLabel) }
            Span({
                style {
                    typography(TextLevel.BodyMedium)
                    fg(WLColors.OnSurface)
                    property("font-weight", "600")
                }
            }) { Text(s.priceUnits(pricing.teaserPreviewCost + pricing.teaserRenderCost)) }
        }
    }
}

@Composable
private fun TeaserStepCells(num: Int, title: String, time: String, costLabel: String) {
    Span({
        style {
            typography(TextLevel.BodySmall)
            fg(WLColors.OnSurface)
        }
    }) { Text("$num. $title") }
    Span({
        style {
            typography(TextLevel.LabelSmall)
            fg(WLColors.OnSurfaceVariant)
        }
    }) { Text(time) }
    Span({
        style {
            typography(TextLevel.BodySmall)
            fg(WLColors.OnSurface)
            property("min-width", "44px")
            property("text-align", "right")
        }
    }) { Text(costLabel) }
}

@Composable
private fun LoadingRow() {
    val s = LocalStrings.current
    Div({
        style {
            flexRow(gapPx = 10, align = AlignItems.Center)
            property("padding", "12px 0")
        }
    }) {
        MdCircularProgress()
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(s.promoGeneratingLabel) }
    }
}

@Composable
private fun ErrorLine(message: String) {
    Span({
        style {
            typography(TextLevel.BodySmall)
            fg(WLColors.Error)
            property("text-align", "center")
        }
    }) { Text(message) }
}

/** Tab display order is enum declaration order via `PromoTab.entries`.
 *  Cover first — most visual, most «wow on landing the tab», sells best.
 *  Video second — second-strongest visual. Annotation last — text, less
 *  immediate punch. */
private enum class PromoTab(val title: (Strings) -> String) {
    Cover({ it.promoCoverTitle }),
    Video({ it.promoVideoTitle }),
    Annotation({ it.promoAnnotationTitle }),
}

private enum class TabStatus { NONE, LOADING, DONE }

/** Small inline status indicator for a promo tab — done (green check),
 *  loading (skeleton pulse dot), or absent. Sits next to the tab title
 *  so the user can scan the strip without entering each tab. */
@Composable
private fun TabStatusDot(status: TabStatus) {
    when (status) {
        TabStatus.DONE -> Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.Success)
                property("line-height", "1")
            }
        }) { Text("✓") }
        TabStatus.LOADING -> Div({
            classes("wl-skeleton")
            style {
                property("width", "8px")
                property("height", "8px")
                property("border-radius", "50%")
            }
        })
        TabStatus.NONE -> {}
    }
}

/**
 * Pill-кнопка всегда выглядит активной. Если действие недоступно,
 * под кнопкой проявляется строчка-объяснение и через 4 секунды тает
 * обратно — кратко, но достаточно чтобы прочесть.
 *
 * `disabledHint` — что именно показать, когда кнопка кликнута в
 * disabled-режиме. Default — `promoMaterialsHintLocked` («Сначала
 * запустите ИИ-анализ»). Callers с другими preconditions (например,
 * обязательное поле формы) должны передать осмысленный текст —
 * иначе у пользователя возникает ложное чувство, что виноват анализ.
 */
@Composable
private fun GenerateButton(
    label: String,
    enabled: Boolean,
    onClick: () -> Unit,
    /** When set, the price-tag suffix is appended to [label] and a
     *  dragon icon is rendered after it. Pass 0 for free actions —
     *  the suffix becomes «· бесплатно» and the icon is suppressed. */
    priceAmount: Int? = null,
    disabledHint: String? = null,
    onDisabledClick: () -> Unit = {},
) {
    val s = LocalStrings.current
    val showNudge = LocalNudge.current
    val effectiveHint = disabledHint ?: s.promoMaterialsHintLocked

    val fullLabel = label + (priceAmount?.let { s.priceTag(it) } ?: "")

    // Container is NOT a flex row — when the label wraps to two lines
    // the dragon icon must stick to the end of the last text line, not
    // float in the vertical centre as a separate flex child would.
    // Inline-block on DragonIcon makes it flow as the last «character»
    // of the label and wrap with it.
    Div({
        style {
            property("padding", "10px 16px")
            property("border-radius", "120px")
            property("text-align", "center")
            property("box-sizing", "border-box")
            typography(TextLevel.LabelLarge)
            bg(WLColors.Secondary)
            fg(WLColors.OnSecondary)
            clickableCursor()
            fillWidth()
        }
        onClick {
            if (enabled) {
                onClick()
            } else {
                showNudge(effectiveHint)
                onDisabledClick()
            }
        }
    }) {
        // Non-breaking space before the icon glues it to the last
        // word, so the icon never lands alone on a wrapped line.
        Text(fullLabel)
        if (priceAmount != null && priceAmount > 0) {
            Text("\u00A0")
            DragonIcon(sizePx = 16, tint = DragonTint.DARK)
        }
    }

}

// ── Preview-композаблы ──────────────────────────────────────────────────

/**
 * Аннотация — HTML тело из бэкенда (body + stakes + cliffhanger) плюс
 * pill-переключатель вариантов хука. Выбранный хук рисуется жирным
 * над телом. selectedHook сбрасывается на 0 при смене `result` через
 * `key(result.hashCode())` чтобы регенерация не оставалась на индексе,
 * которого больше нет.
 */
@Composable
private fun AnnotationPreview(
    result: AnnotationResult,
    selectedHook: Int,
    onSelectHook: (Int) -> Unit,
) {
    val s = LocalStrings.current
    val hooks = result.hooks
    val body = result.annotation
    val safeIndex = selectedHook.coerceIn(0, (hooks.size - 1).coerceAtLeast(0))

    Div({
        style {
            flexCol(gapPx = 12)
            fillWidth()
        }
    }) {
        // Hook variant selector — sits at the TOP of the annotation
        // preview so the user can pick the opening line first, then
        // read the body with that variant in place. Used to live at
        // the bottom which made the bold lead above and the chips
        // below feel disconnected.
        if (hooks.size > 1) {
            Div({
                style {
                    flexRow(gapPx = 8, align = AlignItems.Center)
                    property("flex-wrap", "wrap")
                }
            }) {
                Span({
                    style {
                        typography(TextLevel.LabelSmall)
                        fg(WLColors.OnSurfaceVariant)
                    }
                }) { Text(s.promoHookVariantsLabel) }
                hooks.forEachIndexed { idx, _ ->
                    val isSelected = idx == safeIndex
                    Div({
                        style {
                            property("padding", "4px 10px")
                            property("border-radius", "120px")
                            typography(TextLevel.LabelSmall)
                            if (isSelected) {
                                bg(WLColors.Tertiary)
                                fg(WLColors.OnTertiary)
                            } else {
                                bg(WLColors.SurfaceContainerLow)
                                fg(WLColors.OnSurfaceVariant)
                            }
                            clickableCursor()
                        }
                        onClick { onSelectHook(idx) }
                    }) { Text("#${idx + 1}") }
                }
            }
        }

        if (hooks.isNotEmpty()) {
            Div({
                style {
                    typography(TextLevel.BodyLarge)
                    fg(WLColors.OnSurface)
                    property("font-weight", "700")
                }
            }) { Text(hooks[safeIndex]) }
        }

        if (body.isNotBlank()) {
            MarkdownBlock(body) {
                style {
                    typography(TextLevel.BodyMedium)
                    markdownTheme()
                }
            }
        }
    }
}

